import { useEffect, useRef, type RefObject } from "react";

/**
 * Manages focus trapping, Escape key, body scroll lock, focus restoration, and background `inert` for
 * modals. Returns a ref to attach to the modal container.
 *
 * Shared because it is accessibility behaviour, not presentation: two apps whose modals trap focus
 * differently is a defect, not a style choice (see app-boundaries.md). It began as a copy in the employer
 * app and a second copy in the broker app; the copies already disagreed with the menus they served, since
 * neither counted links as focusable.
 *
 * @param onClose called on Escape
 * @param initialFocusRef optional element to focus first. When omitted the container itself receives focus
 *   (requires the caller to set tabIndex={-1} on the container element) — preferred over auto-focusing a
 *   text input because that pops the virtual keyboard on mobile the moment the modal opens.
 */
export function useModalFocus(
  onClose: () => void,
  initialFocusRef?: RefObject<HTMLElement | null>,
) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Capture the element that had focus before the modal opened so we can restore it on close — otherwise
    // focus jumps to <body> and keyboard users lose their place.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Make everything outside the modal inert so AT cursor + tab order don't wander into background content.
    // We walk top-level body children and mark any that don't contain the modal — that covers the common
    // React-tree case without needing a portal, and a portaled modal exactly.
    const inertTargets: HTMLElement[] = [];
    for (const node of Array.from(document.body.children)) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.contains(container)) continue;
      if (node.hasAttribute("inert")) continue;
      node.setAttribute("inert", "");
      inertTargets.push(node);
    }

    // Links included: a navigation menu is mostly links, and a trap that cannot see them lets Tab escape
    // past the last one.
    const FOCUSABLE =
      'a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Focus the requested initial target, otherwise the container itself — see JSDoc for why we avoid
    // auto-focusing the first focusable.
    if (initialFocusRef?.current) {
      initialFocusRef.current.focus();
    } else {
      container.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      // Re-query on each Tab so dynamically shown/hidden fields are included
      const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
      for (const node of inertTargets) node.removeAttribute("inert");
      // Restore focus to wherever it was before the modal opened. Guard against the previously-focused
      // element having been removed from the DOM.
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [onClose, initialFocusRef]);

  return containerRef;
}
